<script lang="ts">
  import Typography from "../Typography.svelte";
  import { TypographyType } from "../../lib/constants/typographyMap";
  import Select from "./Select.svelte";
  import Input from "./Input.svelte";
  import { getMonthsOptions, getInputLabels, validateDate } from "../../lib/helpers/steps/checkAge";
  import { type CustomField } from "../../lib/stores/settings";
  import { getCustomCopy } from "../../lib/helpers/userDetails";
  import { sessionStore } from "../../lib/stores/session";
  import { format, parse } from "date-fns";
  import isEmpty from "lodash/isEmpty";

  export let customField: CustomField;
  export let copy: Record<string, string>;
  export let invalidCustomFields: Record<string, "missing" | "invalid" | undefined>;

  // The value can be a boolean because of the custom field types though if it's date input it will be a string
  export let value: string | undefined | boolean = undefined;

  const dateComponents: Record<string, string> = {
    month: "",
    day: "",
    year: ""
  };

  // For the custom field date input, we need to handle each date component separately,
  // until the user has filled all the date components. Only then we can validate the date and save it.
  // The same will be true when the user returns to the user details to edit the details.
  // When the user returns to the user details to edit the details, we need to set the date component values
  // to the values that were saved in the userStore.
  const getDateComponentValue = (
    dateComponent: "month" | "day" | "year",
    dateFunction: "getMonth" | "getDate" | "getFullYear",
    // We need to increment the date component value by 1 for the month only because the month is 0-indexed
    increment: number = 0
  ) => {
    if (dateComponents[dateComponent]) {
      return dateComponents[dateComponent];
    } else if (value) {
      dateComponents[dateComponent] = `${new Date(value as string)[dateFunction]() + increment}`;
      return dateComponents[dateComponent];
    } else {
      return "";
    }
  };

  const setDateComponentValue = (dateComponent: "month" | "day" | "year", newValue: string) => {
    dateComponents[dateComponent] = newValue;
    const month = dateComponents.month;
    const day = dateComponents.day;
    const year = dateComponents.year;
    const isEveryDateInputFieldFilled =
      ["day", "month", "year"]
        .map((key) => dateComponents[key])
        .every((value) => !isEmpty(value));

    const isValidDate = validateDate({ month, day, year });

    if (isEveryDateInputFieldFilled && isValidDate && year?.length > 3) {
      // Safari doesn't support the date format yyyy-MM-dd
      // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
      // hence we need to use date-fns to parse the date
      const dob = parse(
        ["year", "month", "day"].map((key) => dateComponents[key]).join("-"),
        "yyyy-MM-dd",
        new Date()
      );

      value = format(dob, "yyyy-MM-dd");
    } else {
      value = "";
    }
  };

  $: monthsOptions = getMonthsOptions($sessionStore.language);
  $: inputLabels = getInputLabels($sessionStore.language);
</script>

<Typography
  mobile={TypographyType.Headline3Medium}
  desktop={TypographyType.Headline5}
  --font-color="var(--color-grayscale-90)"
>
  {copy[`customField-${customField.identifier}Copy`] ?? customField.name}
</Typography>

<div class="flex flex-col lg:flex-row gap-4">
  <div class="w-full lg:w-fit min-w-48">
    <Select
      value={getDateComponentValue("month", "getMonth", 1)}
      onChange={(value) => setDateComponentValue("month", value)}
      optionList={monthsOptions}
      placeholder={inputLabels.month}
    />
  </div>
  <div class="w-full lg:w-fit min-w-32">
    <Input
      type="number"
      inputProps={{ min: 1, max: 31 }}
      value={getDateComponentValue("day", "getDate", 0)}
      onChange={(value) => setDateComponentValue("day", `${value}`)}
      placeholder={inputLabels.day}
    />
  </div>
  <div class="w-full lg:w-fit min-w-32">
    <Input
      type="number"
      inputProps={{ max: new Date().getFullYear() }}
      value={getDateComponentValue("year", "getFullYear", 0)}
      onChange={(value) => setDateComponentValue("year", `${value}`)}
      placeholder={inputLabels.year}
    />
  </div>
</div>

<div class="lg:mt-4 flex flex-col">
  {#if invalidCustomFields[customField.name]}
    <div class="lg:-mt-6 h-2">
      <Typography
        mobile={TypographyType.Body2Regular}
        desktop={TypographyType.Body2Medium}
        --font-color="var(--color-red-50)">
        <span aria-label="error-message">
          {getCustomCopy(customField, invalidCustomFields[customField.name], copy)}
        </span>
      </Typography>
    </div>
  {/if}
</div>
